import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
  useQueryClient,
} from '@tanstack/react-query';

import { API_BASE_URL } from '../config/config';
import {
  ApiResponse_boolean,
  MemberHomeCourseClient,
  MemberHomeCourseSummaryDTO,
} from '../services/apiClient';
import axiosInstance from '../services/axiosInstance';

// Takes the course id, resolves with the backend's ApiResponse<bool>.
type MemberCourseMutation = UseMutationResult<
  ApiResponse_boolean,
  unknown,
  string
>;

const apiClient = new MemberHomeCourseClient(API_BASE_URL, axiosInstance);

const MY_HOME_COURSES_KEY = ['myHomeCourses'];

// The logged-in member's own home courses (already resolved with course name/address/logo,
// see GET /member/me/home-courses on the backend), A to Z by course name.
export const useMyHomeCourses = (): UseQueryResult<
  MemberHomeCourseSummaryDTO[]
> =>
  useQuery<MemberHomeCourseSummaryDTO[]>({
    queryKey: MY_HOME_COURSES_KEY,
    queryFn: async () => {
      const response = await apiClient.getMyHomeCourses();
      return response?.data || [];
    },
    staleTime: 5 * 60 * 1000,
  });

// Add and remove go to the member-scoped PUT/DELETE routes, which resolve the member from the
// token, so nothing here sends a member id. Both are idempotent on the backend. A member may have
// several home courses, so adding one never replaces another.
export const useAddMyHomeCourse = (): MemberCourseMutation => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (courseId: string) =>
      apiClient.addCourseToMyHomeCourses(courseId),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: MY_HOME_COURSES_KEY });
    },
  });
};

// "Make visible / hide" on one home club. Hidden is the backend's Private flag.
export const useSetMyHomeCourseHidden = (): UseMutationResult<
  ApiResponse_boolean,
  unknown,
  { courseId: string; hidden: boolean }
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ courseId, hidden }) =>
      apiClient.setMyHomeCourseVisibility(courseId, { hidden }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: MY_HOME_COURSES_KEY });
    },
  });
};

// "Make primary" on one home club: the one the profile shows. The backend clears the others and
// shows the course if it was hidden.
export const useSetMyPrimaryHomeCourse = (): MemberCourseMutation => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (courseId: string) =>
      apiClient.setMyPrimaryHomeCourse(courseId),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: MY_HOME_COURSES_KEY });
    },
  });
};

export const useRemoveMyHomeCourse = (): MemberCourseMutation => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (courseId: string) =>
      apiClient.removeCourseFromMyHomeCourses(courseId),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: MY_HOME_COURSES_KEY });
    },
  });
};
